/**
 * Theme switcher (light / dark / system) — localStorage-only, no server.
 *
 * Convenzione di markup: la classe `dark` viene applicata su
 * `document.documentElement` quando il tema attivo e' dark; Tailwind
 * `darkMode: ["class"]` (tailwind.config.ts) reagisce di conseguenza.
 */

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "dpanel:ui:theme";

/** Lettura tema persistito (default "system"). */
export function readThemeMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "light" || raw === "dark" || raw === "system") {
    return raw;
  }
  return "system";
}

/** Applica + persiste tema. Sicuro da chiamare client-side ovunque. */
export function applyThemeMode(mode: ThemeMode): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, mode);
  const isDark =
    mode === "dark" ||
    (mode === "system" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.classList.toggle("dark", isDark);
}

/**
 * Inizializza il tema al boot (chiamato in `src/main.tsx` prima del
 * render React per evitare flash white→dark).
 */
export function initTheme(): void {
  applyThemeMode(readThemeMode());
  if (typeof window !== "undefined" && readThemeMode() === "system") {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => applyThemeMode("system"));
  }
}
